import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

/**
 * Wrap password input field into this component tags.
 * Add template variable to main password input field `#passwordInput`.
 *
 * OR
 *
 * Add `portal-password-to-text` as an attribute to password input field and provide
 * "input group append template" only with buttons, and password repeat input ref.
 */
@Component({
  selector: 'portal-password-to-text, [portal-password-to-text]',
  templateUrl: './password-to-text.component.html',
  styles: [],
  standalone: false,
})
export class PasswordToTextComponent implements OnDestroy, AfterViewInit {
  // if additional buttons are to be provided as in `password strength` then this template is used
  @ViewChild('inputGroupAppendTmpl') inputGroupAppendTmpl!: TemplateRef<any>;

  // provided as content to the component, check the `login-form.component` for usage
  @ContentChild('passwordInput', { read: ElementRef }) passwordInput: ElementRef<HTMLInputElement>;

  @Input()
  inputGroupAppendButtonsTmpl?: TemplateRef<any>;

  @Input()
  passwordRepeatInput?: HTMLInputElement;

  @Input()
  toggleButtonClass: string = 'text-muted';

  public plainText = false;
  public toggleBtnVisible = false;

  private data = '';
  private unlistenFns: Array<() => void> = [];
  private inputElems: Array<HTMLInputElement> = [];

  constructor(
    private viewContainerRef: ViewContainerRef,
    private renderer2: Renderer2
  ) {}

  ngOnDestroy(): void {
    this.unlistenFns.forEach((fn) => fn());
  }

  ngAfterViewInit(): void {
    if (this.passwordInput == undefined) {
      // the password input is the view container
      this.passwordInput = this.viewContainerRef.element;

      // create embedded view in the next tick. change detection complains because of `toggleBtnTmpl` (undefined and then not)
      setTimeout(() => {
        this.viewContainerRef.createEmbeddedView(this.inputGroupAppendTmpl);
      }, 0);
    }

    if (this.passwordInput != null) {
      this.inputElems.push(this.passwordInput.nativeElement);

      this.unlistenFns.push(
        this.renderer2.listen(this.passwordInput.nativeElement, 'input', this.onInput.bind(this))
      );
    }

    if (this.passwordRepeatInput != null) {
      this.inputElems.push(this.passwordRepeatInput);
    }
  }

  toggle(): void {
    this.plainText = !this.plainText;
    if (this.plainText === true) {
      this.inputElems.forEach((el) => this.renderer2.setAttribute(el, 'type', 'text'));
    } else {
      this.inputElems.forEach((el) => this.renderer2.setAttribute(el, 'type', 'password'));
    }
    // move the focus from the toggle button to the password input
    this.passwordInput.nativeElement.focus();
  }

  onInput() {
    this.data = this.passwordInput.nativeElement.value;
    if (this.data.length > 0) {
      this.toggleBtnVisible = true;
      this.renderer2.addClass(this.passwordInput.nativeElement, 'input-filed-right-padding');
    } else {
      this.toggleBtnVisible = false;
      this.renderer2.removeClass(this.passwordInput.nativeElement, 'input-filed-right-padding');
    }
  }

  /**
   * Resets the values to original state.
   *
   */
  public reset(complete = true) {
    this.toggleBtnVisible = false;
    if (complete === true) {
      this.data = '';
      this.inputElems.forEach((el) => this.renderer2.setAttribute(el, 'type', 'password'));
      this.plainText = false;
    } else {
      this.inputElems.forEach((el) => this.renderer2.setAttribute(el, 'type', 'text'));
    }
  }
}
